import { put, call, takeEvery, takeLatest } from 'redux-saga/effects';

import {
  createActivity,
  updateActivity,
  deleteActivity,
  fetchActivities,
  fetchActivityById,
} from '../apis/activity';
import {
  getActivitiesAction,
  getActivitiesSuccess,
  getActivitiesFailure,
  createActivityAction,
  updateActivityAction,
  deleteActivityAction,
  createActivitySuccess,
  createActivityFailure,
  updateActivitySuccess,
  updateActivityFailure,
  deleteActivitySuccess,
  deleteActivityFailure,
  getActivityByIdAction,
  getActivityByIdSuccess,
  getActivityByIdFailure,
} from '../slices/activity';

// VALIDATION_ERROR carries the field-level reason under details, e.g. { day: ["..."] }.
const firstDetail = (details) => {
  if (!details || typeof details !== 'object') return null;
  const [field, messages] = Object.entries(details)[0] || [];
  const text = Array.isArray(messages) ? messages[0] : messages;
  return field && text ? `${field}: ${text}` : null;
};

const errorMessage = (error, fallback) =>
  firstDetail(error.response?.data?.error?.details) ||
  error.response?.data?.error?.message ||
  error.response?.data?.message ||
  error.response?.data?.detail ||
  error.message ||
  fallback;

function* getActivities(action) {
  try {
    const response = yield call(fetchActivities, action.payload);
    yield put(getActivitiesSuccess(response));
  } catch (error) {
    yield put(getActivitiesFailure(errorMessage(error, 'Failed to fetch activities')));
  }
}

function* handleCreateActivity(action) {
  try {
    yield call(createActivity, action.payload);
    yield put(createActivitySuccess());
  } catch (error) {
    yield put(createActivityFailure(errorMessage(error, 'Failed to create activity')));
  }
}

function* handleUpdateActivity(action) {
  try {
    const { id, data } = action.payload;
    const response = yield call(updateActivity, id, data);
    yield put(updateActivitySuccess(response));
  } catch (error) {
    yield put(updateActivityFailure(errorMessage(error, 'Failed to update activity')));
  }
}

function* handleDeleteActivity(action) {
  try {
    const payload = action.payload || {};
    const id = typeof payload === 'string' ? payload : payload.id;
    const refetchParams = typeof payload === 'object' ? payload.refetchParams : undefined;
    yield call(deleteActivity, id);
    yield put(deleteActivitySuccess());
    if (refetchParams) yield put(getActivitiesAction(refetchParams));
  } catch (error) {
    yield put(deleteActivityFailure(errorMessage(error, 'Failed to delete activity')));
  }
}

function* getActivityById(action) {
  try {
    const response = yield call(fetchActivityById, action.payload);
    yield put(getActivityByIdSuccess(response));
  } catch (error) {
    yield put(getActivityByIdFailure(errorMessage(error, 'Failed to fetch activity')));
  }
}

export default function* activitySaga() {
  yield takeLatest(getActivitiesAction.type, getActivities);
  yield takeEvery(getActivityByIdAction.type, getActivityById);
  yield takeEvery(createActivityAction.type, handleCreateActivity);
  yield takeEvery(updateActivityAction.type, handleUpdateActivity);
  yield takeEvery(deleteActivityAction.type, handleDeleteActivity);
}
